import React, { useState } from "react";

const DataScience = () => {
  const [expandedSections, setExpandedSections] = useState({});

  const toggleSection = (section) => {
    setExpandedSections((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const syllabusData = {
    courseOverview: {
      title: "Course Overview",
      content: (
        <>
          <p className="font-bold text-sm">Objective:</p> Learn the fundamentals
          of Data Science using Python and apply them to real-world problems.
          <p className="font-bold text-sm">Duration:</p> 30 days, 1 hour per
          day.
          <p className="font-bold text-sm">Prerequisites:</p> Basic knowledge of
          programming (preferably Python), mathematics (statistics), and
          interest in data analysis.
          <p className="font-bold text-sm">Tools Needed:</p> Python 3.x, Jupyter
          Notebook, NumPy, Pandas, Matplotlib, Scikit-learn, and a code editor
          (e.g., VS Code).
        </>
      ),
    },
    dailySchedule: {
      title: "Daily Schedule",
      days: [
        {
          day: "Day 1: Introduction to Data Science",
          objective:
            "Understand the basics of Data Science and its applications.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is Data Science? Overview and lifecycle.</li>
                <li>Role of Python in Data Science.</li>
                <li>Setting up the Python environment.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Install Python and Jupyter Notebook.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 2: Python Basics for Data Science",
          objective: "Learn Python fundamentals for data manipulation.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Variables, data types, and operators.</li>
                <li>Control structures (if-else, loops).</li>
                <li>Functions and basic input/output.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Write a simple Python script with loops.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 3: Introduction to NumPy",
          objective: "Understand NumPy for numerical computations.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Arrays and matrices in NumPy.</li>
                <li>Basic operations and indexing.</li>
                <li>Mathematical functions.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create and manipulate a NumPy array.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 4: Data Manipulation with Pandas",
          objective: "Learn to handle data with Pandas.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Series and DataFrames.</li>
                <li>Loading and exploring datasets.</li>
                <li>Basic data cleaning.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Load a CSV file and explore its data.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 5: Data Cleaning with Pandas",
          objective: "Master data cleaning techniques.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Handling missing values.</li>
                <li>Removing duplicates and outliers.</li>
                <li>Data type conversion.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Clean a dataset with missing values.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 6: Data Visualization with Matplotlib",
          objective: "Create basic visualizations using Matplotlib.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to data visualization.</li>
                <li>Line plots, bar charts, and histograms.</li>
                <li>Customizing plots.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Plot a bar chart from a dataset.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 7: Advanced Visualization with Seaborn",
          objective: "Explore advanced visualization with Seaborn.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to Seaborn.</li>
                <li>Heatmaps, pair plots, and box plots.</li>
                <li>Styling and customization.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a heatmap for a dataset.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 8: Introduction to Statistics",
          objective: "Understand statistical concepts for Data Science.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Mean, median, mode, and standard deviation.</li>
                <li>Probability distributions.</li>
                <li>Correlation and covariance.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Calculate statistics on a dataset.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 9: Exploratory Data Analysis (EDA)",
          objective: "Perform EDA to understand data patterns.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is EDA and its importance.</li>
                <li>Summary statistics and visualizations.</li>
                <li>Identifying trends and outliers.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Conduct EDA on a sample dataset.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 10: Data Preprocessing",
          objective: "Prepare data for machine learning.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Feature scaling and normalization.</li>
                <li>Encoding categorical variables.</li>
                <li>Splitting data into train and test sets.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Preprocess a dataset for modeling.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 11: Introduction to Machine Learning",
          objective: "Understand the basics of machine learning.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Supervised vs. unsupervised learning.</li>
                <li>Overview of Scikit-learn.</li>
                <li>Types of machine learning problems.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Install Scikit-learn and explore its documentation.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 12: Linear Regression",
          objective: "Implement linear regression models.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding linear regression.</li>
                <li>Cost function and gradient descent.</li>
                <li>Implementing with Scikit-learn.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a linear regression model.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 13: Logistic Regression",
          objective: "Learn logistic regression for classification.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to logistic regression.</li>
                <li>Sigmoid function and decision boundaries.</li>
                <li>Implementation with Scikit-learn.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Train a logistic regression model.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 14: Decision Trees",
          objective: "Understand and implement decision trees.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>How decision trees work.</li>
                <li>Entropy and information gain.</li>
                <li>Building a decision tree with Scikit-learn.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a decision tree classifier.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 15: Random Forests",
          objective: "Explore ensemble learning with random forests.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to random forests.</li>
                <li>Bagging and feature randomness.</li>
                <li>Implementation with Scikit-learn.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Train a random forest model.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 16: K-Nearest Neighbors (KNN)",
          objective: "Learn the KNN algorithm.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Understanding KNN classification.</li>
                <li>Distance metrics and k-value selection.</li>
                <li>Implementing KNN with Scikit-learn.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Build a KNN classifier.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 17: Support Vector Machines (SVM)",
          objective: "Understand SVM for classification.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to SVM.</li>
                <li>Hyperplanes and support vectors.</li>
                <li>Implementation with Scikit-learn.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Train an SVM model.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 18: Clustering with K-Means",
          objective: "Learn unsupervised learning with K-Means.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to clustering.</li>
                <li>K-Means algorithm and centroids.</li>
                <li>Implementation with Scikit-learn.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Perform K-Means clustering on a dataset.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 19: Dimensionality Reduction with PCA",
          objective: "Understand PCA for data reduction.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to PCA.</li>
                <li>Eigenvalues and eigenvectors.</li>
                <li>Applying PCA with Scikit-learn.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Reduce dimensions of a dataset with PCA.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 20: Model Evaluation Metrics",
          objective: "Learn to evaluate machine learning models.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Accuracy, precision, recall, and F1-score.</li>
                <li>Confusion matrix and ROC curve.</li>
                <li>Cross-validation techniques.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Evaluate a model using metrics.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 21: Hyperparameter Tuning",
          objective: "Optimize machine learning models.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What are hyperparameters?</li>
                <li>Grid search and random search.</li>
                <li>Using Scikit-learn for tuning.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Tune a model with grid search.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 22: Introduction to Time Series Analysis",
          objective: "Explore time series data analysis.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is time series data?</li>
                <li>Stationarity and trends.</li>
                <li>Basic forecasting techniques.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Analyze a time series dataset.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 23: Natural Language Processing (NLP) Basics",
          objective: "Learn the basics of NLP with Python.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to NLP.</li>
                <li>Text preprocessing (tokenization, stemming).</li>
                <li>Using NLTK or spaCy.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Preprocess a text dataset.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 24: Working with APIs and Web Scraping",
          objective: "Fetch data from APIs and the web.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Introduction to APIs and requests library.</li>
                <li>Web scraping with BeautifulSoup.</li>
                <li>Ethical considerations.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Scrape data from a website.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 25: Introduction to Deep Learning",
          objective: "Understand the basics of deep learning.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>What is deep learning?</li>
                <li>Neural networks and layers.</li>
                <li>Introduction to TensorFlow or Keras.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Set up TensorFlow and run a basic model.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 26: Final Project – Planning",
          objective: "Plan the final Data Science project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min discussion):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Overview of the final project.</li>
                <li>
                  Suggested project ideas (e.g., Sales prediction, sentiment
                  analysis).
                </li>
                <li>Planning data collection and analysis.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Create a project plan and dataset outline.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 27: Final Project – Data Collection and Cleaning",
          objective: "Collect and clean data for the project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Sourcing data (APIs, web scraping, datasets).</li>
                <li>Cleaning and preprocessing steps.</li>
                <li>Exploratory analysis.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Prepare the project dataset.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 28: Final Project – Modeling",
          objective: "Build and train a model for the project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Selecting an appropriate model.</li>
                <li>Training and evaluating the model.</li>
                <li>Hyperparameter tuning.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Implement the project model.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 29: Final Project – Visualization and Insights",
          objective: "Visualize results and derive insights.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Creating visualizations for results.</li>
                <li>Interpreting model outputs.</li>
                <li>Documenting findings.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Visualize and summarize project results.</li>
              </ul>
            </>
          ),
        },
        {
          day: "Day 30: Final Project – Presentation",
          objective: "Present the final Data Science project.",
          topics: (
            <>
              <p className="font-bold text-sm">
                Topics Covered (30 min theory, 30 min practical):
              </p>
              <ul className="list-disc pl-4 text-xs">
                <li>Preparing a live demo or report.</li>
                <li>Discussing challenges and solutions.</li>
                <li>Presenting to peers.</li>
              </ul>
              <p className="font-bold text-sm">Hands-On:</p>
              <ul className="list-disc pl-4 text-xs">
                <li>Present the completed project.</li>
              </ul>
            </>
          ),
        },
      ],
    },
    learningOutcomes: {
      title: "Learning Outcomes",
      content: (
        <>
          <p className="text-sm">
            By the end of this course, learners will be able to:
          </p>
          <ul className="list-disc pl-4 text-xs">
            <li>Manipulate and analyze data using Python libraries.</li>
            <li>Build and evaluate machine learning models.</li>
            <li>Create visualizations to communicate insights.</li>
            <li>Complete a Data Science project from start to finish.</li>
          </ul>
        </>
      ),
    },
    finalProjectIdeas: {
      title: "Suggested Final Project Ideas",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>
              <span className="font-bold text-sm">Sales Prediction:</span>{" "}
              Predict sales based on historical data.
            </li>
            <li>
              <span className="font-bold text-sm">Sentiment Analysis:</span>{" "}
              Analyze sentiment from social media data.
            </li>
            <li>
              <span className="font-bold text-sm">Customer Segmentation:</span>{" "}
              Cluster customers using K-Means.
            </li>
          </ul>
        </>
      ),
    },
    additionalNotes: {
      title: "Additional Notes",
      content: (
        <>
          <ul className="list-disc pl-4 text-xs">
            <li>Encourage practice with real-world datasets (e.g., Kaggle).</li>
            <li>
              Provide resources (e.g., Python docs, Scikit-learn tutorials).
            </li>
            <li>Use discussion time for Q&A and troubleshooting.</li>
          </ul>
          <p className="text-sm">
            This syllabus progresses from foundational skills to practical Data
            Science applications over 30 days.
          </p>
        </>
      ),
    },
  };

  // Function to handle enrollment (replace with actual enrollment logic)
  const handleEnroll = () => {
    alert("Enroll Now clicked! (Add your enrollment logic here)");
  };

  return (
    <div className="min-h-screen bg-gray-100 font-sans">
      {/* Hero Section */}
      <header className="bg-gradient-to-r from-red-600 to-indigo-600 py-8 text-center">
        <h1 className="text-2xl font-bold mb-1 text-white">
          Data Science with Python Syllabus
        </h1>
        <p className="text-sm text-white">
          Master Data Science with Python (30 Hours – 1 Hour Per Day)
        </p>
      </header>

      {/* Training Availability Banner */}
      <div className="fixed top-34 left-80v right-4 bg-yellow-200 border border-yellow-400 rounded-lg p-3 text-center shadow-md z-10">
        <h2 className="text-sm font-semibold text-indigo-800">
          Training Available
        </h2>
        <p className="text-xs text-gray-700">Online Class | Physical Class</p>
        <button
          onClick={handleEnroll}
          className="mt-2 px-4 py-1 bg-orange-600 text-white text-xs rounded-lg hover:bg-orange-700 transition duration-300"
        >
          Enroll Now
        </button>
      </div>

      <div className="max-w-4xl mx-auto p-2 pt-16 pb-16">
        {/* Course Overview */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("courseOverview")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.courseOverview.title}
            <span>{expandedSections["courseOverview"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["courseOverview"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.courseOverview.content}
            </div>
          )}
        </div>

        {/* Daily Schedule */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("dailySchedule")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.dailySchedule.title}
            <span>{expandedSections["dailySchedule"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["dailySchedule"] && (
            <div className="p-1 bg-white">
              {syllabusData.dailySchedule.days.map((day, index) => (
                <div
                  key={index}
                  className="mb-1 border border-gray-100 rounded-lg"
                >
                  <h3
                    onClick={() => toggleSection(`day${index}`)}
                    className="bg-gray-50 p-1 cursor-pointer flex justify-between items-center text-xs font-medium text-gray-600 hover:bg-gray-100"
                  >
                    {day.day}
                    <span>{expandedSections[`day${index}`] ? "▲" : "▼"}</span>
                  </h3>
                  {expandedSections[`day${index}`] && (
                    <div className="p-1 bg-white text-gray-600 leading-tight">
                      <p className="font-bold text-sm">Objective:</p>{" "}
                      {day.objective}
                      {day.topics}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Learning Outcomes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("learningOutcomes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.learningOutcomes.title}
            <span>{expandedSections["learningOutcomes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["learningOutcomes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.learningOutcomes.content}
            </div>
          )}
        </div>

        {/* Final Project Ideas */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("finalProjectIdeas")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.finalProjectIdeas.title}
            <span>{expandedSections["finalProjectIdeas"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["finalProjectIdeas"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.finalProjectIdeas.content}
            </div>
          )}
        </div>

        {/* Additional Notes */}
        <div className="mb-2 border border-gray-200 rounded-lg">
          <h2
            onClick={() => toggleSection("additionalNotes")}
            className="bg-gray-100 p-1 cursor-pointer flex justify-between items-center text-sm font-semibold text-gray-700 hover:bg-gray-200"
          >
            {syllabusData.additionalNotes.title}
            <span>{expandedSections["additionalNotes"] ? "▲" : "▼"}</span>
          </h2>
          {expandedSections["additionalNotes"] && (
            <div className="p-1 bg-white text-gray-600 leading-tight">
              {syllabusData.additionalNotes.content}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DataScience;
